import { useEffect, useState, useMemo, type CSSProperties } from "react";
import { supabase } from "../../lib/supabase";
import { callFn } from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";
import { usePhoneDetail } from "../../lib/usePhoneDetail";
import PhoneBackButton from "../../components/admin/PhoneBackButton";

interface ContactSubmission {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  message: string;
  status: "new" | "read" | "replied";
  created_at: string;
}

export default function AdminCRM() {
  // Phone (<768): one column, and the inbox or the open message, never both (Phase 4b).
  const isPhone = useIsMobile(768);
  const phone = usePhoneDetail(isPhone);
  // Messages State
  const [messages, setMessages] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search & Filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Selected Message for detail view / reply
  const [selectedMsg, setSelectedMsg] = useState<ContactSubmission | null>(null);
  const [replyBody, setReplyBody] = useState("");
  const [sendingReply, setSendingSendingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySuccess, setReplySuccess] = useState(false);

  // Load submissions from the database (public.contact_submissions)
  async function loadMessages() {
    try {
      const { data, error: fetchErr } = await supabase
        .from("contact_submissions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      if (fetchErr) throw fetchErr;
      setMessages(data || []);
    } catch (err: any) {
      console.error("Failed to load contact submissions:", err);
      setError("Could not load contact messages from the inbox.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMessages();
  }, []);

  // Filter messages dynamically using useMemo
  const filteredMessages = useMemo(() => {
    const query = q.trim().toLowerCase();
    return messages.filter((m) => {
      // Status filter
      if (statusFilter !== "all" && m.status !== statusFilter) return false;

      // Search text filter
      if (query) {
        const name = `${m.first_name} ${m.last_name || ""}`.toLowerCase();
        const email = m.email.toLowerCase();
        const msgText = m.message.toLowerCase();
        if (!name.includes(query) && !email.includes(query) && !msgText.includes(query)) return false;
      }

      return true;
    });
  }, [messages, q, statusFilter]);

  // Mark a message as read when opened
  async function handleOpenMessage(msg: ContactSubmission) {
    setSelectedMsg(msg);
    setReplyBody("");
    setReplyError(null);
    setReplySuccess(false);

    if (msg.status === "new") {
      try {
        const { error: patchErr } = await supabase
          .from("contact_submissions")
          .update({ status: "read" })
          .eq("id", msg.id);

        if (!patchErr) {
          // Update status in local memory array silently
          setMessages((prev) =>
            prev.map((x) => (x.id === msg.id ? { ...x, status: "read" } : x))
          );
        }
      } catch (err) {
        console.error("Failed to mark message as read:", err);
      }
    }
  }

  // Send the email reply using the reply-contact Edge Function
  async function handleSendReply() {
    if (!selectedMsg || !replyBody.trim()) return;
    setSendingSendingReply(true);
    setReplyError(null);
    setReplySuccess(false);

    try {
      await callFn("reply-contact", {
        id: selectedMsg.id,
        body: replyBody,
      });

      // Update status to 'replied' in local memory & database
      await supabase
        .from("contact_submissions")
        .update({ status: "replied" })
        .eq("id", selectedMsg.id);

      setMessages((prev) =>
        prev.map((x) => (x.id === selectedMsg.id ? { ...x, status: "replied" } : x))
      );
      setSelectedMsg((prev) => (prev ? { ...prev, status: "replied" } : null));

      setReplySuccess(true);
      setReplyBody("");
    } catch (err: any) {
      console.error("Failed to send email reply:", err);
      setReplyError(err.message || "Failed to deliver email reply.");
    } finally {
      setSendingSendingReply(false);
    }
  }

  function fmtDate(iso: string) {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  if (loading) {
    return <div style={styles.loader}>Loading Contact Inbox…</div>;
  }

  const newCount = messages.filter((m) => m.status === "new").length;

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="crm" badgeCounts={{ crm: messages.length }} />
      <header style={styles.header}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", width: "100%" }}>
          <h1 style={styles.title}>Contact Inbox</h1>
          <span style={{ ...styles.inboxCount, color: newCount > 0 ? "var(--gold)" : "var(--muted)" }}>
            {newCount > 0 ? `${newCount} New Messages` : "Inbox up to date"}
          </span>
        </div>
        <p style={styles.subtitle}>Review public website contact submissions and send email replies directly.</p>
      </header>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={isPhone ? { ...styles.layoutGrid, gridTemplateColumns: "minmax(0, 1fr)" } : styles.layoutGrid}>
        {/* Left Side: Inbox List */}
        {phone.showList && <div style={styles.inboxPanel}>
          <div style={styles.toolbar}>
            <input
              type="text"
              placeholder="Search sender, email, message..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={styles.search}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={styles.select}
            >
              <option value="all">All</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
            </select>
          </div>

          <div style={isPhone ? { ...styles.list, ...phoneListScroll } : styles.list}>
            {filteredMessages.map((m) => {
              const name = `${m.first_name} ${m.last_name || ""}`;
              const isNew = m.status === "new";
              const isReplied = m.status === "replied";

              return (
                <button
                  key={m.id}
                  style={{
                    ...styles.listItem,
                    ...(selectedMsg?.id === m.id ? styles.listItemSelected : {}),
                  }}
                  // On a phone, re-opening the message that's already open keeps a half-written
                  // reply (Back doesn't discard it) instead of clearing it.
                  onClick={() => { if (!(isPhone && selectedMsg?.id === m.id)) void handleOpenMessage(m); phone.openDetail(); }}
                >
                  <div style={styles.itemHeader}>
                    <strong style={{ fontSize: 14, fontWeight: isNew ? 800 : 600 }}>{name}</strong>
                    <span style={styles.itemDate}>{fmtDate(m.created_at)}</span>
                  </div>
                  <div style={styles.itemEmail}>{m.email}</div>
                  <div style={styles.itemBody}>
                    {m.message.substring(0, 90)}
                    {m.message.length > 90 ? "..." : ""}
                  </div>
                  <div style={styles.badgeRow}>
                    {isNew ? (
                      <span style={styles.badgeGreen}>New</span>
                    ) : isReplied ? (
                      <span style={styles.badgeMuted}>Replied</span>
                    ) : (
                      <span style={styles.badgeGold}>Read</span>
                    )}
                  </div>
                </button>
              );
            })}
            {filteredMessages.length === 0 && (
              <div style={styles.emptyInbox}>No messages in this scope.</div>
            )}
          </div>
        </div>}

        {/* Right Side: Message Detail & Reply Composer */}
        {phone.showDetail && <div style={styles.detailPanel} ref={phone.detailRef}>
          {phone.phoneOpen && <PhoneBackButton label="Inbox" onClick={phone.back} />}
          {selectedMsg ? (
            <div style={styles.detailContainer}>
              <div style={styles.detailHeader}>
                <h2 style={styles.detailTitle}>
                  Message from {selectedMsg.first_name} {selectedMsg.last_name || ""}
                </h2>
                <span style={styles.detailDate}>{fmtDate(selectedMsg.created_at)}</span>
              </div>
              <div style={styles.detailEmail}>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${selectedMsg.email}`} style={styles.emailLink}>
                  {selectedMsg.email}
                </a>
              </div>

              <div style={styles.messageBox}>
                <p style={styles.messageText}>{selectedMsg.message}</p>
              </div>

              {/* Reply Section */}
              <div style={styles.replyBox}>
                <h3 style={styles.replyTitle}>Draft Email Reply</h3>
                {replySuccess ? (
                  <div style={styles.successBanner}>✓ Reply delivered successfully!</div>
                ) : (
                  <>
                    <textarea
                      placeholder="Write your email reply here..."
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      disabled={sendingReply || selectedMsg.status === "replied"}
                      style={styles.replyArea}
                    />
                    {replyError && <div style={styles.errorArea}>{replyError}</div>}
                    <button
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyBody.trim() || selectedMsg.status === "replied"}
                      style={styles.sendBtn}
                    >
                      {sendingReply ? "Sending..." : selectedMsg.status === "replied" ? "Already Replied" : "Send Reply Email"}
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={styles.emptyDetail}>
              <div style={styles.emptyIcon}>✉</div>
              <p>Select a contact submission from the inbox to read details and compose email replies.</p>
            </div>
          )}
        </div>}
      </div>
    </div>
  );
}

// Phones: the inbox scrolls with the page, not inside its own 70vh box. A nested scroller is
// awkward on a phone, and the box is removed while a message is open, so its position was lost
// on Back (usePhoneDetail restores the page's scroll).
const phoneListScroll: CSSProperties = { maxHeight: "none", overflowY: "visible" };

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 16px",
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    minHeight: "100vh",
  },
  header: { marginBottom: 24 },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  inboxCount: {
    fontSize: 14,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  layoutGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr",
    gap: 24,
    width: "100%",
  },
  inboxPanel: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  toolbar: {
    display: "flex",
    gap: 12,
  },
  search: {
    flex: 1,
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: "10px 16px",
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    outline: "none",
  },
  select: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: "10px 16px",
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    outline: "none",
    cursor: "pointer",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: "70vh",
    overflowY: "auto",
  },
  listItem: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: 16,
    color: "var(--text, #F7F5F0)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    cursor: "pointer",
    textAlign: "left",
    outline: "none",
    transition: "border-color 0.2s ease",
  },
  listItemSelected: {
    borderColor: "var(--gold, #F9AF36)",
  },
  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    width: "100%",
  },
  itemDate: {
    fontSize: 11,
    color: "var(--muted, #A39D98)",
  },
  itemEmail: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  itemBody: {
    fontSize: 13,
    lineHeight: 1.4,
    color: "var(--text, #F7F5F0)",
    marginTop: 4,
  },
  badgeRow: {
    display: "flex",
    gap: 6,
    marginTop: 4,
  },
  badgeGreen: {
    fontSize: 10,
    fontWeight: "bold",
    background: "rgba(39, 174, 96, 0.15)",
    color: "#86efac",
    padding: "2px 8px",
    borderRadius: 6,
    textTransform: "uppercase",
  },
  badgeMuted: {
    fontSize: 10,
    fontWeight: "bold",
    background: "var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "2px 8px",
    borderRadius: 6,
    textTransform: "uppercase",
  },
  badgeGold: {
    fontSize: 10,
    fontWeight: "bold",
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold, #F9AF36)",
    padding: "2px 8px",
    borderRadius: 6,
    textTransform: "uppercase",
  },
  emptyInbox: {
    padding: "40px",
    textAlign: "center",
    color: "var(--muted, #A39D98)",
    fontStyle: "italic",
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
  },
  detailPanel: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 16,
    padding: 24,
    minHeight: "65vh",
  },
  detailContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  detailHeader: {
    borderBottom: "1px solid var(--border, #231D19)",
    paddingBottom: 16,
    marginBottom: 16,
  },
  detailTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  detailDate: {
    fontSize: 12,
    color: "var(--muted, #A39D98)",
  },
  detailEmail: {
    fontSize: 14,
    marginBottom: 20,
    color: "var(--text, #F7F5F0)",
  },
  emailLink: {
    color: "var(--gold, #F9AF36)",
    textDecoration: "underline",
  },
  messageBox: {
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
  },
  messageText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap",
    color: "var(--text, #F7F5F0)",
  },
  replyBox: {
    borderTop: "1px solid var(--border, #231D19)",
    paddingTop: 20,
  },
  replyTitle: {
    fontSize: 14,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "var(--muted, #A39D98)",
    marginBottom: 12,
  },
  replyArea: {
    width: "100%",
    minHeight: 140,
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: 16,
    color: "var(--text, #F7F5F0)",
    fontSize: 14,
    lineHeight: 1.5,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    marginBottom: 16,
  },
  sendBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "12px 24px",
    fontSize: 13,
    fontWeight: "bold",
    textTransform: "uppercase",
    cursor: "pointer",
    letterSpacing: "0.4px",
    transition: "all 0.2s",
  },
  emptyDetail: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    color: "var(--muted, #A39D98)",
    padding: "40px",
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    color: "var(--border, #231D19)",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.12)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "var(--red, #FF8A7A)",
    borderRadius: 8,
    padding: "12px 16px",
    marginBottom: 20,
    fontSize: 13,
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.12)",
    border: "1px solid rgba(39, 174, 96, 0.3)",
    color: "#86efac",
    borderRadius: 8,
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: "bold",
  },
  errorArea: {
    color: "var(--red, #FF8A7A)",
    fontSize: 13,
    marginBottom: 12,
  },
  loader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    color: "var(--muted, #A39D98)",
    fontSize: 14,
  },
};
